import React from 'react';
import { motion } from 'framer-motion';
import { Wrench, Check, ArrowRight, Clock, ArrowLeft, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import useMetaTags from '../hooks/useMetaTags';
import useStructuredData from '../hooks/useStructuredData';
import { NavbarHTAG } from '../components/NavbarHTAG';
import { FooterHTAG } from '../components/FooterHTAG';

export const DepannageService = () => {
  // Meta tags SEO
  useMetaTags({
    title: 'Dépannage Informatique Urgent en Ardennes | HTAG Facility',
    description: 'Dépannage informatique urgent, support IT, intervention rapide. Services de dépannage pour PME, associations en Ardennes. Basé à Château-Porcien, intervention sur site ou à distance.',
    keywords: 'dépannage informatique, support IT, assistance technique, urgence informatique, Ardennes, Château-Porcien',
    url: 'https://htagfacility.fr/services/depannage',
    image: 'https://htagfacility.fr/og-service-depannage.png',
    type: 'article'
  });

  // Structured Data - Service
  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Dépannage Informatique Ultra Réactif',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'HTAG Facility',
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': 'Château-Porcien',
        'addressLocality': 'Château-Porcien',
        'addressRegion': 'Ardennes',
        'postalCode': '08320',
        'addressCountry': 'FR'
      }
    },
    'description': 'Dépannage informatique urgent avec intervention rapide sur site ou à distance',
    'areaServed': ['FR-08', 'Ardennes', 'Grand Est'],
    'offers': {
      '@type': 'Offer',
      'description': 'Service de dépannage informatique réactif'
    }
  });

  return (
    <div className="bg-[#181C25] text-white lg:ml-16">
      <NavbarHTAG />

      {/* Bouton retour */}
      <div className="pt-32 px-4 lg:px-8 relative z-10">
        <div className="container mx-auto mb-8">
          <Link
            to="/?scroll=services"
            className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors font-semibold hover:translate-x-1 duration-300"
          >
            <ArrowLeft size={18} />
            <span>Retour aux services</span>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pb-24 px-4 lg:px-8 bg-gradient-to-b from-[#212635] to-[#181C25]">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            {/* Contenu */}
            <div>
              <h1 className="text-5xl lg:text-6xl font-montserrat font-bold mb-6">
                Dépannage & Fibre <span className="text-orange-400">Ultra réactif</span>
              </h1>
              <p className="text-xl text-gray-300 mb-4 leading-relaxed">
                Support technique immédiat pour minimiser vos arrêts. Panne, installation ou formation, nous réagissons vite.
              </p>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                Votre infrastructure IT est critique pour votre activité. Eric propose une assistance réactive, disponible 24/7 en cas d'urgence, pour assurer la continuité de vos services. <strong className="text-gray-300">Support en visio avec intervention sur site si nécessaire.</strong>
              </p>
              <a
                href="/?scroll=contact"
                className="inline-flex items-center space-x-3 px-8 py-4 rounded-lg bg-orange-600 hover:bg-orange-700 font-montserrat font-semibold transition-all"
              >
                <span>Contacter le support</span>
                <ArrowRight size={20} />
              </a>
            </div>

            {/* Visuel */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative h-96 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border-2 border-orange-500/30 overflow-hidden"
            >
              <img 
                src="https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=500&h=500&fit=crop" 
                alt="Support Technique" 
                className="w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#181C25] to-transparent flex items-end justify-center pb-6">
                <p className="text-gray-300 font-montserrat text-lg font-semibold">Support Technique Réactif</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services détails */}
      <section className="py-24 px-4 lg:px-8">
        <div className="container mx-auto">
          {/* Avantages de réactivité */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <h2 className="text-4xl font-montserrat font-bold mb-12 text-white">
              Assistance informatique ultra réactive
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Clock,
                  title: 'Réactivité',
                  desc: 'Intervention dans les 2 heures - assistance à distance ou sur site'
                },
                {
                  icon: Check,
                  title: 'Expertise',
                  desc: 'Diagnostique rapide et solutions pérennes, pas de rustines'
                },
                {
                  icon: Wrench,
                  title: 'Disponibilité',
                  desc: 'Support 24/7 pour les urgences, heures de bureau régulières'
                },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="spotlight-card p-8 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 hover:border-orange-500 transition-all text-center"
                  >
                    <Icon className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                    <h3 className="font-montserrat font-bold text-white text-lg mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-400">{item.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Services détails */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <h2 className="text-4xl font-montserrat font-bold mb-12 text-white">
              Les services inclus
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Assistance Informatique',
                  items: [
                    'Dépannage à distance ou sur site',
                    'Résolution de bugs applicatifs',
                    'Gestion des accès utilisateur',
                    'Mise à jour logiciels/système',
                    'Sauvegarde et récupération données'
                  ]
                },
                {
                  title: 'Formation & Documentation',
                  items: [
                    'Formation utilisateur personnalisée',
                    'Création de manuels d\'utilisation',
                    'Tutoriels vidéo et guides',
                    'Accompagnement changements IT',
                    'Documentation technique'
                  ]
                },
                {
                  title: 'Maintenance Préventive',
                  items: [
                    'Monitoring système périodique',
                    'Maintenance postes de travail',
                    'Tests de sauvegarde réguliers',
                    'Rapports d\'activité mensuels',
                    'Optimisation performances'
                  ]
                },
              ].map((service, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="spotlight-card p-8 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 hover:border-orange-500 transition-all"
                >
                  <h3 className="font-montserrat font-bold text-lg text-orange-400 mb-4">
                    {service.title}
                  </h3>
                  <ul className="space-y-3">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Service Urgent en Ardennes */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mb-24 p-12 rounded-2xl bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-500/30"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="text-red-400" size={28} />
                  <h3 className="text-2xl font-montserrat font-bold text-white">
                    Dépannage Urgent en Ardennes 24/7
                  </h3>
                </div>
                <p className="text-gray-300 mb-4">
                  Basé à <strong>Château-Porcien</strong> en Ardennes, nous offrons un service de dépannage réactif et professionnel. Intervention rapide sur site ou assistance à distance selon vos besoins. Assistance continuée pour minimiser les interruptions de service.
                </p>
                <p className="text-gray-400 text-sm">
                  <strong>Disponibilité :</strong> Support continu pendant les heures de travail et intervention d'urgence sur demande.
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="text-center p-6 rounded-xl bg-red-600/10 border border-red-500/30">
                  <p className="text-red-400 font-montserrat font-bold text-sm mb-2">INTERVENTION RAPIDE</p>
                  <p className="text-white font-bold text-lg">Ardennes</p>
                  <p className="text-gray-400 text-sm">Région Grand Est</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Final */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center p-12 rounded-2xl bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/30"
          >
            <h3 className="text-3xl font-montserrat font-bold mb-4 text-white">
              Besoin d'une intervention immédiate ?
            </h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Contactez Eric pour une assistance technique rapide. Panne serveur, installation, ou formation, nous réagissons vite.
            </p>
            <a
              href="/?scroll=contact"
              className="inline-flex items-center space-x-3 px-10 py-4 rounded-lg bg-orange-600 hover:bg-orange-700 font-montserrat font-semibold transition-all"
            >
              <span>Contacter maintenant</span>
              <ArrowRight size={20} />
            </a>
          </motion.div>
        </div>
      </section>

      <FooterHTAG />
    </div>
  );
};

export default DepannageService;
